import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { AlertTriangle, ArrowDown, ArrowUp, Ban, CheckCircle2, Download, FileText, Loader2,
  Lock, PenLine, Plus, RefreshCcw, Send, Trash2, UserPlus, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PROC_STATUS, SIGNER_STATUS, SIGNER_ROLES, signerRoleLabel, initials, fmtDateTime, shortHash } from "@/lib/contracts";
import { cn } from "@/lib/utils";
import { useLang } from "@/i18n";
import api from "@/lib/api";
import { toast } from "sonner";

const Card = ({ title, right, children, testid, className }) => (
  <section className={cn("rounded-2xl border border-border bg-surface p-5", className)} data-testid={testid}>
    {title && (
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">{title}</h2>
        {right}
      </div>
    )}
    {children}
  </section>
);

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  return (typeof d === "object" ? d?.message : d) || "Erreur";
};

/* ---------- stepper ---------- */
const Stepper = ({ proc, contract, tf }) => {
  const signers = proc?.signers || [];
  const signed = signers.filter((s) => s.status === "signed").length;
  const done5 = ["signed", "upcoming", "active"].includes(contract.status) && proc?.status === "completed";
  const steps = [
    { label: tf("Document final"), done: !!proc?.document_id,
      sub: proc?.document_id ? tf("Verrouillé") : tf("À sélectionner") },
    { label: tf("Signataires"), done: signers.length > 0,
      sub: signers.length ? `${signers.length} ${tf("configuré(s)")}` : "—" },
    { label: tf("Envoi"), done: !!proc?.sent_at,
      sub: proc?.sent_at ? tf("Invitations envoyées") : tf("En attente") },
    { label: tf("Signature"), done: signers.length > 0 && signed === signers.length && !!proc?.sent_at,
      current: ["sent", "in_progress"].includes(proc?.status),
      sub: proc?.sent_at ? `${signed}/${signers.length} ${tf("signés")}` : "—" },
    { label: tf("Finalisation"), done: done5, sub: done5 ? tf("Terminé") : tf("En attente") },
  ];
  return (
    <div className="rounded-2xl border border-border bg-surface p-4 overflow-x-auto" data-testid="signature-stepper">
      <ol className="flex items-start min-w-[560px]">
        {steps.map((s, i) => (
          <li key={s.label} className="flex-1 flex items-start">
            <div className="flex flex-col items-center text-center flex-1">
              <span className={cn("h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold border",
                s.done ? "bg-green-500/15 text-green-400 border-green-500/40"
                  : s.current ? "bg-amber-500/15 text-amber-400 border-amber-500/40"
                    : "bg-secondary/30 text-muted-foreground border-border")}
                data-testid={`sig-step-${i + 1}`}>
                {s.done ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
              </span>
              <p className={cn("text-xs mt-1.5 font-medium", s.done ? "text-foreground" : s.current ? "text-amber-400" : "text-muted-foreground")}>{s.label}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{s.sub}</p>
            </div>
            {i < steps.length - 1 && <div className={cn("h-px flex-1 mt-3.5", s.done ? "bg-green-500/30" : "bg-secondary/50")} />}
          </li>
        ))}
      </ol>
    </div>
  );
};

/* ---------- signer dialog (add / edit) ---------- */
const SignerDialog = ({ open, onOpenChange, pid, signer, onSaved, tf }) => {
  const empty = { name: "", email: "", role: "player", organization: "", phone: "" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(signer ? { ...empty, ...signer } : empty); }, [signer, open]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async () => {
    setSaving(true);
    try {
      if (signer) await api.patch(`/signature/${pid}/signers/${signer.id}`, form);
      else await api.post(`/signature/${pid}/signers`, form);
      toast.success(signer ? tf("Signataire mis à jour") : tf("Signataire ajouté"));
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]" data-testid="signer-dialog">
        <DialogHeader>
          <DialogTitle>{signer ? tf("Modifier le signataire") : tf("Ajouter un signataire")}</DialogTitle>
          <DialogDescription>{tf("Chaque partie devant signer le document final.")}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3.5 py-1">
          <div className="grid sm:grid-cols-2 gap-3.5">
            <div className="grid gap-1.5">
              <Label>{tf("Nom complet")} *</Label>
              <Input data-testid="signer-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Romain Sissoko" />
            </div>
            <div className="grid gap-1.5">
              <Label>{tf("Rôle")}</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="signer-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SIGNER_ROLES.map((r) => <SelectItem key={r.id} value={r.id}>{tf(r.label)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>{tf("Email")} *</Label>
            <Input type="email" data-testid="signer-email-input" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="email@exemple.fr" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3.5">
            <div className="grid gap-1.5">
              <Label>{tf("Organisation")}</Label>
              <Input data-testid="signer-org-input" value={form.organization || ""} onChange={(e) => setForm((f) => ({ ...f, organization: e.target.value }))} placeholder="Real Madrid CF" />
            </div>
            <div className="grid gap-1.5">
              <Label>{tf("Téléphone")}</Label>
              <Input data-testid="signer-phone-input" value={form.phone || ""} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+33 6…" />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{tf("Annuler")}</Button>
          <Button data-testid="signer-save-btn" onClick={submit} disabled={saving}>
            {saving ? tf("Enregistrement…") : signer ? tf("Enregistrer") : tf("Ajouter")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ---------- send confirmation modal ---------- */
const SendModal = ({ open, onOpenChange, proc, provider, onSent, tf }) => {
  const [confirmed, setConfirmed] = useState(false);
  const [sending, setSending] = useState(false);
  useEffect(() => { if (open) setConfirmed(false); }, [open]);
  const send = async () => {
    if (sending) return;
    setSending(true);
    try {
      await api.post(`/signature/${proc.id}/send`, { confirm: true });
      toast.success(tf("Invitations envoyées"));
      onOpenChange(false);
      onSent?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSending(false); }
  };
  if (!proc) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]" data-testid="send-signature-modal">
        <DialogHeader>
          <DialogTitle>{tf("Envoyer pour signature")}</DialogTitle>
          <DialogDescription>{tf("Vérifiez les éléments avant l'envoi — le document sera verrouillé.")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-xl border border-border p-3 flex items-center gap-3">
            <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
            <div className="min-w-0">
              <p className="font-medium truncate" data-testid="send-doc-name">{proc.document_filename}</p>
              <p className="text-xs text-muted-foreground">SHA-256 {shortHash(proc.document_sha256)}</p>
            </div>
          </div>
          <div className="rounded-xl border border-border p-3">
            <p className="text-xs text-muted-foreground mb-2">
              {proc.signers.length} {tf("signataire(s)")} · {proc.signing_mode === "sequential" ? tf("Signature séquentielle") : tf("Signature parallèle")}
              {proc.expires_at ? ` · ${tf("Expire le")} ${fmtDateTime(proc.expires_at)}` : ""}
            </p>
            <ul className="space-y-1" data-testid="send-recipients">
              {proc.signers.map((s) => (
                <li key={s.id} className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground w-4">{s.order}.</span>
                  <span className="font-medium">{s.name}</span>
                  <span className="text-muted-foreground truncate">{s.email}</span>
                </li>
              ))}
            </ul>
          </div>
          {proc.message && <p className="text-xs text-muted-foreground whitespace-pre-wrap border-l-2 border-border pl-3">{proc.message}</p>}
          {provider?.test_mode && (
            <p className="text-xs rounded-lg bg-amber-500/10 border border-amber-500/25 text-amber-400 px-3 py-2" data-testid="send-test-mode-note">
              {tf("Mode test : aucune invitation réelle ne sera envoyée.")}
            </p>
          )}
          <label className="flex items-start gap-2.5 cursor-pointer" data-testid="send-confirm-check">
            <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} className="mt-0.5" />
            <span className="text-xs text-muted-foreground leading-relaxed">
              {tf("Je confirme avoir vérifié la concordance du document final avec les termes négociés (montants, dates, parties, clauses).")}
            </span>
          </label>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{tf("Annuler")}</Button>
          <Button data-testid="send-confirm-btn" onClick={send} disabled={!confirmed || sending}>
            {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
            {tf("Envoyer les invitations")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ---------- main workspace ---------- */
export const TabSignature = ({ contract, onChanged }) => {
  const { tf } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dlgSigner, setDlgSigner] = useState(null); // null closed, {} add, {...} edit
  const [sendOpen, setSendOpen] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/contracts/${contract.id}/signature`);
      setData(r.data);
    } catch { toast.error(tf("Impossible de charger la signature")); }
    finally { setLoading(false); }
  }, [contract.id, tf]);
  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChanged?.(); };
  const proc = data?.procedure;
  const provider = data?.provider;
  const editable = proc && ["draft", "ready"].includes(proc.status);
  const running = proc && ["sent", "in_progress"].includes(proc.status);
  const signedDocs = useMemo(() => (contract.documents || []).filter((d) => d.doc_kind === "signed_contract"), [contract.documents]);
  const sigEvents = useMemo(() => (contract.events || []).filter((e) =>
    ["signature_procedure_created", "final_version_selected", "signature_sent", "signer_signed", "signer_declined",
      "signer_reminded", "signature_completed", "signed_document_stored", "contract_signed", "contract_upcoming",
      "contract_activated", "signature_cancelled", "signature_expired", "signer_opened"].includes(e.type)).slice(0, 10),
  [contract.events]);

  const createProc = async () => {
    try { await api.post(`/contracts/${contract.id}/signature`); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const lockDoc = async (did) => {
    try { await api.post(`/signature/${proc.id}/document`, { document_id: did }); toast.success(tf("Document final sélectionné")); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const patchProc = async (payload) => {
    try { await api.patch(`/signature/${proc.id}`, payload); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const removeSigner = async (sid) => {
    try { await api.delete(`/signature/${proc.id}/signers/${sid}`); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const move = async (idx, dir) => {
    const ids = proc.signers.map((s) => s.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    try { await api.post(`/signature/${proc.id}/signers/reorder`, { ids }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remind = async (sid, name) => {
    try { await api.post(`/signature/${proc.id}/signers/${sid}/remind`); toast.success(`${tf("Relance envoyée à")} ${name}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const cancel = async () => {
    try { await api.post(`/signature/${proc.id}/cancel`); toast.success(tf("Procédure annulée")); setConfirmCancel(false); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const simulate = async (sid, event) => {
    try { await api.post(`/signature/${proc.id}/test-events`, { signer_id: sid, event }); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (loading) return <div className="flex justify-center py-20" data-testid="signature-loading"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  if (!proc) {
    return (
      <div className="rounded-2xl border border-dashed border-border p-14 text-center" data-testid="signature-empty">
        <PenLine className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
        <p className="text-sm font-medium">{tf("Aucune procédure de signature")}</p>
        <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
          {tf("Préparez le document final, les signataires, puis envoyez les invitations de signature électronique.")}
        </p>
        <Button className="mt-5" onClick={createProc} data-testid="start-signature-btn">
          <Plus className="h-4 w-4 mr-1.5" /> {tf("Démarrer la signature")}
        </Button>
      </div>
    );
  }

  const pStatus = PROC_STATUS[proc.status] || PROC_STATUS.draft;
  const progress = proc.progress || { signed: 0, total: 0, percent: 0 };
  const terminal = ["completed", "declined", "expired", "cancelled", "failed"].includes(proc.status);
  const recon = data?.reconciliation;

  return (
    <div className="space-y-4" data-testid="tab-signature">
      {!provider?.configured && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.07] px-4 py-3 flex items-start gap-3" data-testid="provider-unconfigured-banner">
          <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
          <div className="text-xs leading-relaxed">
            <p className="font-medium text-amber-400">{tf("Signature électronique non configurée")}</p>
            <p className="text-muted-foreground mt-0.5">{tf("Vous pouvez préparer le document et les signataires. L'envoi des invitations nécessite la clé API Yousign.")}</p>
          </div>
        </div>
      )}
      {provider?.test_mode && (
        <div className="rounded-xl border border-sky-500/30 bg-sky-500/[0.07] px-4 py-2.5 text-xs text-sky-400 font-medium" data-testid="provider-test-banner">
          {tf("Mode test actif — les signatures sont simulées, aucune invitation réelle n'est envoyée.")}
        </div>
      )}

      <Stepper proc={proc} contract={contract} tf={tf} />

      <div className="grid lg:grid-cols-12 gap-4">
        {/* RIGHT rail first on mobile (status priority) */}
        <div className="lg:col-span-3 lg:order-3 space-y-4">
          <Card title={tf("Statut de signature")} testid="signature-status-rail"
            right={<span className={cn("inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium", pStatus.cls)} data-testid="procedure-status-badge">{tf(pStatus.label)}</span>}>
            <p className="text-sm font-semibold" data-testid="signature-progress-count">{progress.signed} {tf("sur")} {progress.total} {tf("signés")}</p>
            <div className="h-1.5 rounded-full bg-secondary/50 mt-2 overflow-hidden">
              <div className="h-full rounded-full bg-green-500 transition-all" style={{ width: `${progress.percent}%` }} />
            </div>
            <div className="mt-4 space-y-2 text-xs">
              {proc.sent_at && <p className="flex justify-between"><span className="text-muted-foreground">{tf("Envoyé le")}</span><span>{fmtDateTime(proc.sent_at)}</span></p>}
              {proc.expires_at && <p className="flex justify-between"><span className="text-muted-foreground">{tf("Expire le")}</span><span>{fmtDateTime(proc.expires_at)}</span></p>}
              {proc.completed_at && <p className="flex justify-between"><span className="text-muted-foreground">{tf("Terminé le")}</span><span className="text-green-400">{fmtDateTime(proc.completed_at)}</span></p>}
              <p className="flex justify-between"><span className="text-muted-foreground">{tf("Fournisseur")}</span><span data-testid="provider-label">{tf(provider?.label)}</span></p>
            </div>
            <div className="mt-4 space-y-2">
              {proc.status === "ready" && (
                <Button className="w-full" onClick={() => setSendOpen(true)} disabled={!provider?.configured} data-testid="open-send-modal-btn">
                  <Send className="h-4 w-4 mr-1.5" /> {tf("Envoyer pour signature")}
                </Button>
              )}
              {["draft"].includes(proc.status) && (
                <p className="text-[11px] text-muted-foreground">{tf("Sélectionnez le document final et ajoutez les signataires (email valide) pour pouvoir envoyer.")}</p>
              )}
              {signedDocs[0] && (
                <Button variant="outline" className="w-full" data-testid="download-signed-btn"
                  onClick={() => window.open(`${api.defaults.baseURL}/contracts/${contract.id}/documents/${signedDocs[0].id}/download`, "_blank")}>
                  <Download className="h-4 w-4 mr-1.5" /> {tf("Télécharger le contrat signé")}
                </Button>
              )}
              {terminal && proc.status !== "completed" && (
                <Button className="w-full" variant="outline" onClick={createProc} data-testid="new-procedure-btn">
                  <Plus className="h-4 w-4 mr-1.5" /> {tf("Nouvelle procédure")}
                </Button>
              )}
              {!terminal && (
                confirmCancel ? (
                  <div className="flex gap-2">
                    <Button variant="destructive" size="sm" className="flex-1" onClick={cancel} data-testid="confirm-cancel-btn">{tf("Confirmer l'annulation")}</Button>
                    <Button variant="ghost" size="sm" onClick={() => setConfirmCancel(false)}>{tf("Non")}</Button>
                  </div>
                ) : (
                  <Button variant="ghost" className="w-full text-muted-foreground hover:text-red-400" onClick={() => setConfirmCancel(true)} data-testid="cancel-procedure-btn">
                    <Ban className="h-4 w-4 mr-1.5" /> {tf("Annuler la procédure")}
                  </Button>
                )
              )}
            </div>
          </Card>

          {recon && (
            <Card title={tf("Réconciliation finale")} testid="signature-reconciliation">
              <div className="space-y-2 text-xs">
                <p className="flex justify-between items-center">
                  <span className="text-muted-foreground">{tf("Revue finale")}</span>
                  {recon.review_done
                    ? <span className="text-green-400 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> {tf("Validée")}</span>
                    : <span className="text-amber-400">{tf("À compléter")}</span>}
                </p>
                {recon.reconciliation && (
                  <p className="flex justify-between items-center">
                    <span className="text-muted-foreground">{tf("Termes vs document")}</span>
                    <span className={recon.reconciliation === "reconciled" ? "text-green-400" : "text-amber-400"}>
                      {recon.reconciliation === "reconciled" ? tf("Concordance") : tf("Vérification requise")}
                    </span>
                  </p>
                )}
                <p className="text-[11px] text-muted-foreground leading-relaxed border-t border-border pt-2">
                  {tf("Athlynx Intelligence assiste la comparaison — la validation humaine reste requise.")}
                </p>
              </div>
            </Card>
          )}

          {sigEvents.length > 0 && (
            <Card title={tf("Historique récent")} testid="signature-recent-events">
              <ol className="space-y-2.5">
                {sigEvents.map((e) => (
                  <li key={e.id} className="flex items-start gap-2.5 text-xs">
                    <span className="h-1.5 w-1.5 rounded-full bg-red-500 shrink-0 mt-1" />
                    <div className="min-w-0 flex-1">
                      <p className="leading-snug">{e.label}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{fmtDateTime(e.created_at)}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </Card>
          )}
        </div>

        {/* LEFT: final document */}
        <div className="lg:col-span-4 lg:order-1 space-y-4">
          <Card title={tf("Document final")} testid="signature-document-card"
            right={proc.document_id && <span className="inline-flex items-center gap-1 text-[11px] text-green-400"><Lock className="h-3 w-3" /> {editable ? tf("Sélectionné") : tf("Verrouillé")}</span>}>
            {proc.document_id ? (
              <div className="space-y-3">
                <div className="rounded-xl border border-border p-3.5 flex items-start gap-3">
                  <FileText className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium break-words" data-testid="locked-doc-name">{proc.document_filename}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">SHA-256 <code className="font-mono">{shortHash(proc.document_sha256)}</code></p>
                    {proc.document_locked_at && <p className="text-[11px] text-muted-foreground">{tf("Sélectionné le")} {fmtDateTime(proc.document_locked_at)}</p>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1" data-testid="view-final-doc-btn"
                    onClick={() => window.open(`${api.defaults.baseURL}/contracts/${contract.id}/documents/${proc.document_id}/download`, "_blank")}>
                    <FileText className="h-3.5 w-3.5 mr-1.5" /> {tf("Voir")}
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1" data-testid="download-final-doc-btn"
                    onClick={() => window.open(`${api.defaults.baseURL}/contracts/${contract.id}/documents/${proc.document_id}/download`, "_blank")}>
                    <Download className="h-3.5 w-3.5 mr-1.5" /> {tf("Télécharger")}
                  </Button>
                </div>
                {!editable && (
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    {tf("Le document soumis est immuable pour cette procédure. Toute modification du contrat nécessite d'annuler et de relancer une procédure.")}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground mb-1">{tf("Sélectionnez la version finale à faire signer :")}</p>
            )}
            {editable && (data?.documents || []).length > 0 && (
              <ul className="mt-3 space-y-1.5" data-testid="final-doc-choices">
                {data.documents.slice(0, 8).map((d) => (
                  <li key={d.id}>
                    <button onClick={() => lockDoc(d.id)} data-testid={`select-doc-${d.id}`}
                      className={cn("w-full text-left rounded-lg border px-3 py-2 text-xs flex items-center gap-2.5 transition-colors",
                        proc.document_id === d.id ? "border-green-500/40 bg-green-500/[0.06]" : "border-border hover:border-foreground/25")}>
                      <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="truncate flex-1">{d.filename}</span>
                      <span className="text-muted-foreground shrink-0">v{d.version}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {editable && (data?.documents || []).length === 0 && (
              <p className="text-xs text-muted-foreground rounded-lg border border-dashed border-border p-3 mt-2" data-testid="no-documents-note">
                {tf("Aucun document — ajoutez le contrat final dans l'onglet Documents.")}
              </p>
            )}
          </Card>

          {editable && (
            <Card title={tf("Configuration de l'envoi")} testid="signature-config-card">
              <div className="space-y-3.5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm">{tf("Signature séquentielle")}</p>
                    <p className="text-[11px] text-muted-foreground">{tf("Chaque signataire signe à son tour, dans l'ordre.")}</p>
                  </div>
                  <Switch checked={proc.signing_mode === "sequential"} data-testid="sequential-switch"
                    onCheckedChange={(v) => patchProc({ signing_mode: v ? "sequential" : "parallel" })} />
                </div>
                <div className="grid gap-1.5">
                  <Label className="text-xs">{tf("Date limite de signature (optionnel)")}</Label>
                  <Input type="date" data-testid="expires-input" defaultValue={proc.expires_at ? proc.expires_at.slice(0, 10) : ""}
                    onBlur={(e) => patchProc({ expires_at: e.target.value ? `${e.target.value}T23:59:59+00:00` : null })} />
                </div>
                <div className="grid gap-1.5">
                  <Label className="text-xs">{tf("Message d'invitation (optionnel)")}</Label>
                  <Textarea rows={3} data-testid="message-input" defaultValue={proc.message || ""}
                    placeholder={tf("Message affiché aux signataires…")}
                    onBlur={(e) => e.target.value !== (proc.message || "") && patchProc({ message: e.target.value })} />
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* CENTER: signers */}
        <div className="lg:col-span-5 lg:order-2 space-y-4">
          <Card title={tf("Signataires")} testid="signature-signers-card"
            right={editable && (
              <Button size="sm" variant="outline" className="h-7" onClick={() => setDlgSigner({})} data-testid="add-signer-btn">
                <UserPlus className="h-3.5 w-3.5 mr-1.5" /> {tf("Ajouter")}
              </Button>
            )}>
            {(proc.signers || []).length === 0 ? (
              <p className="text-xs text-muted-foreground rounded-lg border border-dashed border-border p-4 text-center" data-testid="signers-empty">
                {tf("Aucun signataire. Ajoutez les parties devant signer (email requis).")}
              </p>
            ) : (
              <ul className="space-y-2">
                {[...proc.signers].sort((a, b) => a.order - b.order).map((s, idx) => {
                  const meta = SIGNER_STATUS[s.status] || SIGNER_STATUS.pending;
                  return (
                    <motion.li key={s.id} layout initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                      className="rounded-xl border border-border bg-secondary/30 p-3.5" data-testid={`proc-signer-${s.id}`}>
                      <div className="flex items-start gap-3">
                        <div className="h-9 w-9 rounded-full bg-secondary/50 border border-border flex items-center justify-center text-xs font-semibold shrink-0">
                          {initials(s.name)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            {proc.signing_mode === "sequential" && <span className="text-[10px] text-muted-foreground font-mono">#{s.order}</span>}
                            <p className="text-sm font-medium break-words">{s.name}</p>
                            <span className="text-[10px] text-muted-foreground rounded-full border border-border px-1.5 py-0.5">{tf(signerRoleLabel(s.role))}</span>
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-0.5 break-all">{[s.email, s.organization].filter(Boolean).join(" · ")}</p>
                          {s.signed_at && <p className="text-[11px] text-green-400 mt-0.5 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> {tf("Signé le")} {fmtDateTime(s.signed_at)}</p>}
                          {s.declined_at && <p className="text-[11px] text-red-400 mt-0.5 flex items-center gap-1"><XCircle className="h-3 w-3" /> {tf("Refusé le")} {fmtDateTime(s.declined_at)}</p>}
                          {!s.signed_at && !s.declined_at && s.invited_at && <p className="text-[11px] text-muted-foreground mt-0.5">{tf("Invitation envoyée")} {fmtDateTime(s.invited_at)}</p>}
                          {s.last_reminder_at && <p className="text-[11px] text-muted-foreground mt-0.5">{tf("Dernière relance")} {fmtDateTime(s.last_reminder_at)} ({s.reminder_count})</p>}
                        </div>
                        <div className="flex flex-col items-end gap-1.5 shrink-0">
                          <span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium", meta.cls)} data-testid={`proc-signer-status-${s.id}`}>
                            {tf(meta.label)}
                          </span>
                          {editable && (
                            <div className="flex items-center gap-0.5">
                              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`signer-up-${s.id}`}><ArrowUp className="h-3 w-3" /></Button>
                              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => move(idx, 1)} disabled={idx === proc.signers.length - 1} data-testid={`signer-down-${s.id}`}><ArrowDown className="h-3 w-3" /></Button>
                              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setDlgSigner(s)} data-testid={`signer-edit-${s.id}`}><PenLine className="h-3 w-3" /></Button>
                              <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-red-400" onClick={() => removeSigner(s.id)} data-testid={`signer-remove-${s.id}`}><Trash2 className="h-3 w-3" /></Button>
                            </div>
                          )}
                          {running && !s.signed_at && !s.declined_at && (
                            <Button size="sm" variant="outline" className="h-6 text-[11px] px-2" onClick={() => remind(s.id, s.name)} data-testid={`signer-remind-${s.id}`}>
                              <RefreshCcw className="h-3 w-3 mr-1" /> {tf("Relancer")}
                            </Button>
                          )}
                          {running && provider?.test_mode && !s.signed_at && !s.declined_at && (
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" className="h-6 text-[10px] px-1.5 text-sky-400" onClick={() => simulate(s.id, "signed")} data-testid={`simulate-signed-${s.id}`}>
                                {tf("Simuler : signé")}
                              </Button>
                              <Button size="sm" variant="ghost" className="h-6 text-[10px] px-1.5 text-red-400/80" onClick={() => simulate(s.id, "declined")} data-testid={`simulate-declined-${s.id}`}>
                                {tf("refusé")}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.li>
                  );
                })}
              </ul>
            )}
          </Card>
        </div>
      </div>

      <SignerDialog open={dlgSigner !== null} onOpenChange={(v) => !v && setDlgSigner(null)}
        pid={proc.id} signer={dlgSigner?.id ? dlgSigner : null} onSaved={refresh} tf={tf} />
      <SendModal open={sendOpen} onOpenChange={setSendOpen} proc={proc} provider={provider} onSent={refresh} tf={tf} />
    </div>
  );
};
